import { DateAdapter } from "@angular/material/core";
import { BreakpointObserver } from "@angular/cdk/layout";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  HostBinding,
  HostListener,
  inject,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterModule,
} from "@angular/router";
import { MatSidenav, MatSidenavModule } from "@angular/material/sidenav";
import { TranslocoService, TranslocoModule } from "@ngneat/transloco";
import { ChangeTheme } from "../../../core/services/common/changeTheme.service";
import { OverlayContainer } from "@angular/cdk/overlay";
import { AuthenticationService } from "../../../core/services/common/authentication.service";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatListModule } from "@angular/material/list";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { RutaDinamicaService } from "src/app/core/services/ruta-dinamica.service";
import { TreeMenuComponent } from "./tree-menu/tree-menu.component";
import { TreeNode } from "@core/services/tree-builder.service";
import { OpenInNewTabDirective } from "@shared/directives/open-in-new-tab.directive";
import { UserStateService } from "@core/services/common/user-state.service";
import { catchError, EMPTY, filter, startWith, switchMap } from "rxjs";

/** Rutas donde no se muestra toolbar ni sidenav */
const EXCLUDED_ROUTES: string[] = [
  "/login",
  "/olvido",
  "/verificar",
  "/auth-callback",
  "/logout",
];

/** Rutas donde se muestra toolbar pero no sidenav */
const NO_SIDENAV_ROUTES: string[] = [
  "/settings",
  "/ayuda",
];

@Component({
  selector: "sidenav",
  templateUrl: "sidenav.component.html",
  styleUrls: ["sidenav.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatSidenavModule,
    MatIconModule,
    MatMenuModule,
    MatListModule,
    RouterLink,
    TranslocoModule,
    MatToolbarModule,
    MatButtonModule,
    RouterModule,
    TreeMenuComponent,
    OpenInNewTabDirective,
  ],
})
export class SidenavComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly adapter = inject(DateAdapter);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly changeTheme = inject(ChangeTheme);
  private readonly observer = inject(BreakpointObserver);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly rutaDinamicaService = inject(RutaDinamicaService);
  private readonly userState = inject(UserStateService);

  @HostBinding("class") public componentCssClass: string = "";
  @ViewChild(MatSidenav) public sidenav!: MatSidenav;

  public readonly routesTreeData = signal<TreeNode[]>([]);
  public readonly currentUrl = signal<string>("");
  public readonly toolbar = signal<boolean>(false);
  public readonly theme = signal<string>(localStorage.getItem("theme") || "light-theme");
  public readonly sidenavOpenInSideMode = signal<boolean>(false);
  public readonly isMobile = signal<boolean>(false);

  public readonly descnivelportal = this.userState.rol;
  public readonly dusuario = this.userState.dusuario;
  public readonly entorno = this.userState.entorno;

  public readonly showMenuButton = computed(() => {
    const url = this.currentUrl();
    if (this.isExcludedRoute(url)) return false;
    // En mobile siempre mostrar el botón para poder abrir/cerrar el sidenav
    if (this.isMobile()) return true;
    if (this.isNoSidenavRoute(url)) return false;
    // En desktop con sidenav abierta en modo side, no mostrar botón
    return !this.sidenavOpenInSideMode();
  });

  public ngOnInit(): void {
    this.initTheme();
    this.initLanguage();
    this.listenToRouterEvents();
    this.listenToBreakpoints();
    this.listenToThemeChanges();
    this.listenToLanguageChanges();
  }

  public onSetTheme(themeName: string): void {
    const isDark = themeName === "dark-theme";
    const add = isDark ? "dark-theme" : "light-theme";
    const remove = isDark ? "light-theme" : "dark-theme";

    document.body.classList.remove(remove);
    document.body.classList.add(add);

    const overlay = this.overlayContainer.getContainerElement();
    overlay.classList.remove(remove);
    overlay.classList.add(add);

    this.theme.set(themeName);
    localStorage.setItem("theme", themeName);
    this.componentCssClass = themeName;
    this.cdr.markForCheck();
  }

  public logout(): void {
    this.authenticationService.logout();
    this.sidenav.close();
  }

  @HostListener("contextmenu", ["$event"])
  public onRightClick(event: MouseEvent): void {
    event.preventDefault();
  }

  private initTheme(): void {
    const stored = localStorage.getItem("theme");
    if (stored && (stored === "dark-theme" || stored === "light-theme")) {
      this.onSetTheme(stored);
    } else {
      this.onSetTheme("light-theme");
    }
  }

  private initLanguage(): void {
    const lang = localStorage.getItem("activeLanguage");
    if (lang) {
      this.transloco.setActiveLang(lang);
    }
  }

  private listenToRouterEvents(): void {
    this.router.events
      .pipe(
        startWith(new NavigationEnd(0, this.router.url, this.router.url)),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        switchMap((event: NavigationEnd) => {
          const url = event.urlAfterRedirects || event.url;
          this.currentUrl.set(url);

          this.userState.initializeFromSession();
          this.syncRouting(url);

          this.toolbar.set(!this.isExcludedRoute(url) && url !== "/" && url !== "/#/");

          this.updateSidenavForCurrentRoute(url);

          return this.rutaDinamicaService.fetchMenuUsuario().pipe(
            catchError(() => EMPTY)
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const routing = this.userState.routing() || "MANT";
        this.routesTreeData.set(
          this.rutaDinamicaService.getMenuTreeByName(routing)
        );
      });
  }

  private listenToBreakpoints(): void {
    this.observer
      .observe(["(max-width: 800px)", "(max-width: 1150px)"])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        const pantallaChica = res.breakpoints["(max-width: 800px)"];
        const pantallaMediana = res.breakpoints["(max-width: 1150px)"] && !pantallaChica;

        // Setear isMobile ANTES del early return para que showMenuButton funcione
        // incluso si el ViewChild aún no está disponible
        this.isMobile.set(pantallaChica);

        if (!this.sidenav) return;

        if (pantallaChica) {
          this.sidenav.mode = "over";
          this.sidenav.close();
          this.sidenavOpenInSideMode.set(false);
        } else if (pantallaMediana) {
          this.sidenav.mode = "side";
          this.sidenav.close();
          this.sidenavOpenInSideMode.set(false);
        } else {
          this.sidenav.mode = "side";
          this.updateSidenavForCurrentRoute(this.currentUrl());
        }
      });
  }

  private listenToThemeChanges(): void {
    this.changeTheme.changeEmitted$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: string) => this.onSetTheme(data));
  }

  private listenToLanguageChanges(): void {
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lang: string) => {
        const locale = lang === "en" ? "en" : "es-AR";
        this.adapter.setLocale(locale);
      });
  }

  private updateSidenavForCurrentRoute(url: string): void {
    if (!this.sidenav) return;

    const shouldHide = this.isExcludedRoute(url) || this.isNoSidenavRoute(url) || url === "/" || url === "/#/";
    if (shouldHide) {
      this.sidenav.close();
      this.sidenavOpenInSideMode.set(false);
    } else if (this.sidenav.mode === "side") {
      this.sidenav.open();
      this.sidenavOpenInSideMode.set(true);
    }
  }

  private isExcludedRoute(url: string): boolean {
    return EXCLUDED_ROUTES.some((route) => url.includes(route));
  }

  private isNoSidenavRoute(url: string): boolean {
    return NO_SIDENAV_ROUTES.some((route) => url.includes(route));
  }

  private syncRouting(url: string): void {
    if (url.startsWith("/mantenimiento")) {
      this.userState.setRouting("MANT");
    }
  }
}
